import { App, Button, Form, Input, Switch, Typography } from 'antd';
import { useEffect } from 'react';
import {
  usePatchAssistantMutation,
  type Assistant,
  type AssistantPatch,
} from '@entities/assistant';
import { extractErrorMessage } from '@shared/api';

const { Text } = Typography;

interface Props {
  assistant: Assistant;
}

interface FormValues {
  name: string;
  prompt: string;
  show_images: boolean;
}

/**
 * Редактирование имени, промпта и настройки картинок ассистента.
 *
 * Локализация и gpt_model намеренно НЕ редактируются — после создания
 * провайдеров поменять нельзя без пересоздания ассистента.
 *
 * Пустой промпт → null в payload — бэк сбросит до base_model_prompt.
 *
 * show_images (задача A) — тумблер: показывать ли картинки из базы знаний
 * в ответах бота. По умолчанию true. Отправляется в patch только при
 * изменении.
 */
export function EditAssistantForm({ assistant }: Props) {
  const { message } = App.useApp();
  const [form] = Form.useForm<FormValues>();
  const mutation = usePatchAssistantMutation(assistant.id);

  useEffect(() => {
    form.setFieldsValue({
      name: assistant.name,
      prompt: assistant.prompt ?? '',
      show_images: assistant.show_images ?? true,
    });
  }, [assistant.id, assistant.name, assistant.prompt, assistant.show_images, form]);

  const handleSubmit = async (values: FormValues) => {
    const patch: AssistantPatch = {};
    const newName = values.name.trim();
    const newPrompt = values.prompt.trim();
    const currentPrompt = assistant.prompt ?? '';

    if (newName !== assistant.name) {
      patch.name = newName;
    }
    if (newPrompt !== currentPrompt) {
      // Пустая строка → null чтобы вернуться к дефолтному промпту на бэке.
      patch.prompt = newPrompt || null;
    }
    if (values.show_images !== (assistant.show_images ?? true)) {
      patch.show_images = values.show_images;
    }

    if (Object.keys(patch).length === 0) {
      message.info('Нет изменений');
      return;
    }

    try {
      await mutation.mutateAsync(patch);
      message.success('Сохранено');
    } catch (err) {
      message.error(extractErrorMessage(err));
    }
  };

  return (
    <Form<FormValues>
      form={form}
      layout="vertical"
      requiredMark={false}
      onFinish={handleSubmit}
      initialValues={{
        name: assistant.name,
        prompt: assistant.prompt ?? '',
        show_images: assistant.show_images ?? true,
      }}
    >
      <Form.Item
        name="name"
        label="Название"
        rules={[
          { required: true, message: 'Введите название' },
          { max: 100, message: 'Слишком длинное название' },
        ]}
      >
        <Input size="large" />
      </Form.Item>

      <Form.Item
        name="prompt"
        label="Инструкция для ассистента"
        extra="Оставьте пустым — будет использована стандартная инструкция."
      >
        <Input.TextArea rows={6} maxLength={10000} showCount />
      </Form.Item>

      <Form.Item
        name="show_images"
        label="Картинки в ответах"
        valuePropName="checked"
        extra="Бот прикрепляет картинки из базы знаний к ответам. Отключите, если картинки бывают нерелевантными."
      >
        <Switch
          checkedChildren="Вкл"
          unCheckedChildren="Выкл"
        />
      </Form.Item>

      <Form.Item style={{ marginBottom: 0 }}>
        <Button type="primary" htmlType="submit" loading={mutation.isPending}>
          Сохранить
        </Button>
      </Form.Item>
    </Form>
  );
}
